/**
 * A persisted nutrition block, read onto the nutrition family's own model.
 *
 * WHAT A BLOCK CAN AND CANNOT PROVE. A live search hands the family the
 * vendor's own food or recipe id and the poster's measured pixels. A block
 * carries neither and neither is invented: a row's card key is scoped to the
 * block it came out of — the only scope a persisted item has — and an image is
 * carried as its address with no intrinsic pair, which is what the kit's asset
 * shape means by omitting them.
 *
 * THE UNITS RIDE THE RECORD. The two endpoints disagree about their four
 * micronutrients — a food's are metric amounts, a recipe's are percentages of
 * daily intake — and the block keeps them under two different keys for exactly
 * that reason. The basis a panel is drawn with therefore comes from the key the
 * figures arrived under, never from which record the row is: a food serving
 * with no micronutrient line at all carries no panel rather than four zeroes
 * under a guessed basis.
 */
import {
  AttachResultShape,
  NutritionAttachKind,
  type AttachResult,
  type NutritionAttachMicros,
  type NutritionAttachResult,
  type NutritionAttachServing,
  type ParsedAttachResults,
} from "@cabal/shared/domain/attached-result/index.js";
/**
 * The family's vocabulary comes from its types LEAF rather than its barrel: the
 * barrel re-exports components that import a stylesheet, and this module's own
 * test runs under `node --import tsx --test`, which has no CSS loader.
 */
import {
  CnutritioncardKind,
  CnutritioncardMicroBasis,
  type CnutritioncardData,
  type CnutritioncardIngredient,
  type CnutritioncardMicronutrients,
  type CnutritioncardServing,
} from "@/components/ui/Cnutritioncard/cnutritioncard.types";

/** What separates a row's position from its own name in a card key. */
const KEY_SEPARATOR = ":";

const KIND_BY_ATTACH_KIND: Record<NutritionAttachKind, CnutritioncardKind> = {
  [NutritionAttachKind.Food]: CnutritioncardKind.Food,
  [NutritionAttachKind.Recipe]: CnutritioncardKind.Recipe,
};

/** The row's identity inside its own block. */
function keyOf(input: { index: number; name: string }): string {
  return `${String(input.index)}${KEY_SEPARATOR}${input.name}`;
}

/**
 * The four, under the basis the block itself named by which line carried them.
 *
 * A serving that stated neither line carries no panel — silence about the
 * micronutrients, which is a different claim from four zeroes.
 */
function micronutrientsOf(input: {
  serving: NutritionAttachServing;
}): CnutritioncardMicronutrients | null {
  const measured: readonly {
    readonly basis: CnutritioncardMicroBasis;
    readonly micros: NutritionAttachMicros | null;
  }[] = [
    {
      basis: CnutritioncardMicroBasis.MetricAmount,
      micros: input.serving.micros,
    },
    {
      basis: CnutritioncardMicroBasis.DailyPercent,
      micros: input.serving.dailyIntake,
    },
  ];
  const stated = measured.find((candidate) => candidate.micros != null);
  if (stated?.micros == null) return null;
  return {
    basis: stated.basis,
    vitaminA: stated.micros.vitaminA,
    vitaminC: stated.micros.vitaminC,
    calcium: stated.micros.calcium,
    iron: stated.micros.iron,
  };
}

function servingOf(input: {
  serving: NutritionAttachServing;
  index: number;
}): CnutritioncardServing {
  const serving = input.serving;
  return {
    key: keyOf({ index: input.index, name: serving.measure }),
    description: serving.measure,
    metricAmount: serving.metricAmount,
    metricUnit: serving.metricUnit,
    nutrients: {
      calories: serving.macros.calories,
      carbohydrate: serving.macros.carbohydrate,
      protein: serving.macros.protein,
      fat: serving.macros.fat,
      saturatedFat: serving.macros.saturatedFat,
      transFat: serving.macros.transFat,
      polyunsaturatedFat: serving.macros.polyunsaturatedFat,
      monounsaturatedFat: serving.macros.monounsaturatedFat,
      fiber: serving.macros.fiber,
      sugar: serving.macros.sugar,
      addedSugars: serving.macros.addedSugars,
      cholesterol: serving.macros.cholesterol,
      sodium: serving.macros.sodium,
      potassium: serving.macros.potassium,
    },
    micronutrients: micronutrientsOf({ serving }),
  };
}

/** One ingredient as the block holds it: the line the author wrote, and no
 *  vendor handle behind it — a persisted block prints the measured line and
 *  nothing that would resolve into a panel of its own. */
function ingredientOf(line: string): CnutritioncardIngredient {
  return { foodId: null, name: line, description: null };
}

function rowOf(input: {
  result: NutritionAttachResult;
  index: number;
}): CnutritioncardData {
  const result = input.result;
  return {
    key: keyOf({ index: input.index, name: result.name }),
    kind: KIND_BY_ATTACH_KIND[result.kind],
    name: result.name,
    brand: result.brand,
    vendorKind: result.foodKind,
    summary: result.nutritionSummary,
    description: result.description,
    href: result.linkUrl,
    images: result.photoUrls.map((src) => ({ src, alt: result.name })),
    servings: result.servings.map((serving, index) =>
      servingOf({ serving, index }),
    ),
    numberOfServings: result.numberOfServings,
    preparationTimeMinutes: result.preparationTimeMinutes,
    cookingTimeMinutes: result.cookingTimeMinutes,
    gramsPerPortion: result.gramsPerPortion,
    types: result.types,
    categories: result.categories,
    directions: result.directions,
    ingredients: result.ingredients.map(ingredientOf),
  };
}

function isNutritionResult(
  result: AttachResult,
): result is AttachResult & { readonly shape: AttachResultShape.Nutrition } {
  return result.shape === AttachResultShape.Nutrition;
}

/**
 * Every nutrition row of one parsed block, on the family's model.
 *
 * A block whose items did not read as this pillar's shape yields nothing at
 * all: the shared reader has already degraded it to the generic arm, and the
 * card falls back to the lossless render rather than drawing an empty set.
 */
export function cattachedresultNutrition(input: {
  block: ParsedAttachResults;
}): readonly CnutritioncardData[] {
  return input.block.results
    .filter(isNutritionResult)
    .map((result, index) => rowOf({ result, index }));
}
